import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, Car, Package } from "lucide-react";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");

const th = "text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4";
const td = "py-2.5 pr-4 text-sm text-slate-700 border-t border-slate-100 align-top";

export default function AdminPanel() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [parcels, setParcels] = useState([]);
  const [reTarget, setReTarget] = useState(null);
  const [reDate, setReDate] = useState("");

  const reactivate = async () => {
    try {
      await api.post(`/admin/${reTarget.kind}/${reTarget.id}/reactivate`, reDate ? { date: reDate } : {});
      const kind = reTarget.kind;
      setReTarget(null);
      setReDate("");
      if (kind === "trips") api.get("/admin/trips").then((r) => setTrips(r.data)).catch(() => {});
      else api.get("/admin/parcels").then((r) => setParcels(r.data)).catch(() => {});
    } catch (e) {}
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/trips").then((r) => setTrips(r.data)).catch(() => {});
    api.get("/admin/parcels").then((r) => setParcels(r.data)).catch(() => {});
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/" />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-panel">
      <h1 className="text-3xl md:text-4xl font-bold text-primary mb-8">{t("adminPanel.title")}</h1>
      <Tabs defaultValue="users">
        <TabsList className="bg-secondary/60 rounded-full p-1 mb-8 h-auto flex-wrap">
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full px-5 py-2.5"><Users size={15} className="mr-2" />{t("adminPanel.tabUsers")} ({users.length})</TabsTrigger>
          <TabsTrigger value="trips" data-testid="admin-tab-trips" className="rounded-full px-5 py-2.5"><Car size={15} className="mr-2" />{t("adminPanel.tabTrips")} ({trips.length})</TabsTrigger>
          <TabsTrigger value="parcels" data-testid="admin-tab-parcels" className="rounded-full px-5 py-2.5"><Package size={15} className="mr-2" />{t("adminPanel.tabParcels")} ({parcels.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="bg-white rounded-2xl border border-slate-100 p-5 overflow-x-auto" data-testid="admin-users-table">
            <table className="w-full min-w-[560px]">
              <thead><tr><th className={th}>{t("adminPanel.colName")}</th><th className={th}>{t("adminPanel.colEmail")}</th><th className={th}>{t("adminPanel.colRole")}</th><th className={th}>{t("adminPanel.tabTrips")}</th><th className={th}>{t("adminPanel.tabParcels")}</th><th className={th}>{t("adminPanel.colBookings")}</th><th className={th}>{t("adminPanel.colJoined")}</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-${u.id}`}>
                    <td className={td}>{u.name}</td>
                    <td className={td}>{u.email}</td>
                    <td className={td}>{u.role === "admin" ? <span className="text-xs font-bold text-accent">admin</span> : (u.role || "user")}</td>
                    <td className={td}>{u.trips_count ?? 0}</td>
                    <td className={td}>{u.parcels_count ?? 0}</td>
                    <td className={td}>{u.bookings_count ?? 0}</td>
                    <td className={td}>{fmtDate(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">{t("adminPanel.empty")}</p>}
          </div>
        </TabsContent>

        <TabsContent value="trips">
          <div className="bg-white rounded-2xl border border-slate-100 p-5 overflow-x-auto" data-testid="admin-trips-table">
            <table className="w-full min-w-[640px]">
              <thead><tr><th className={th}>{t("adminPanel.colRoute")}</th><th className={th}>{t("adminPanel.colDate")}</th><th className={th}>{t("adminPanel.colDriver")}</th><th className={th}>{t("adminPanel.colPrice")}</th><th className={th}>{t("adminPanel.colStatus")}</th></tr></thead>
              <tbody>
                {trips.map((trip) => (
                  <tr key={trip.id} data-testid={`admin-trip-${trip.id}`}>
                    <td className={td}>{trip.origin} → {[...(trip.stops || []), trip.destination].join(" → ")}</td>
                    <td className={td}>{trip.date} {trip.time}</td>
                    <td className={td}>{trip.driver?.name || "—"}<br /><span className="text-xs text-slate-400">{trip.driver?.email}</span></td>
                    <td className={td}>{trip.price} €</td>
                    <td className={td}>
                      {trip.status}
                      {trip.status !== "active" && (
                        reTarget?.id === trip.id ? (
                          <span className="flex items-center gap-1 mt-1">
                            <Input type="date" value={reDate} onChange={(e) => setReDate(e.target.value)} className="h-8 rounded-lg text-xs w-32" data-testid="reactivate-date" />
                            <Button size="sm" className="h-8 rounded-full" data-testid="reactivate-confirm" onClick={reactivate}>{t("adminPanel.reactivateOk")}</Button>
                          </span>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 rounded-full text-xs mt-1 ml-2" data-testid={`reactivate-trip-${trip.id}`} onClick={() => { setReTarget({ kind: "trips", id: trip.id }); setReDate(trip.date); }}>{t("adminPanel.reactivate")}</Button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {trips.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">{t("adminPanel.empty")}</p>}
          </div>
        </TabsContent>

        <TabsContent value="parcels">
          <div className="bg-white rounded-2xl border border-slate-100 p-5 overflow-x-auto" data-testid="admin-parcels-table">
            <table className="w-full min-w-[640px]">
              <thead><tr><th className={th}>{t("adminPanel.colRoute")}</th><th className={th}>{t("adminPanel.colDate")}</th><th className={th}>{t("adminPanel.colSender")}</th><th className={th}>{t("adminPanel.colPrice")}</th><th className={th}>{t("adminPanel.colStatus")}</th></tr></thead>
              <tbody>
                {parcels.map((p) => (
                  <tr key={p.id} data-testid={`admin-parcel-${p.id}`}>
                    <td className={td}>{p.origin} → {p.destination}</td>
                    <td className={td}>{p.date}</td>
                    <td className={td}>{p.sender?.name || "—"}<br /><span className="text-xs text-slate-400">{p.sender?.email}</span></td>
                    <td className={td}>{p.price_offer} €</td>
                    <td className={td}>
                      {p.status}
                      {p.status !== "active" && (
                        reTarget?.id === p.id ? (
                          <span className="flex items-center gap-1 mt-1">
                            <Input type="date" value={reDate} onChange={(e) => setReDate(e.target.value)} className="h-8 rounded-lg rounded-lg text-xs w-32" data-testid="reactivate-date" />
                            <Button size="sm" className="h-8 rounded-full" data-testid="reactivate-confirm" onClick={reactivate}>{t("adminPanel.reactivateOk")}</Button>
                          </span>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 rounded-full text-xs mt-1 ml-2" data-testid={`reactivate-parcel-${p.id}`} onClick={() => { setReTarget({ kind: "parcels", id: p.id }); setReDate(p.date); }}>{t("adminPanel.reactivate")}</Button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {parcels.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">{t("adminPanel.empty")}</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
